import React from 'react';
import clsx from 'clsx';
import { withTranslation, WithTranslation } from 'react-i18next';
import ThemeSettingsPanel, { ThemeSettings } from './ThemeSettingsPanel';

type ChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => void;

// Everything inside the editor's superuser-only block. <ThemeSettingsPanel> is
// nested here rather than being a sibling in the container: both halves are
// superuser settings, so only this component carries that name.
interface SuperuserSettingsPanelOwnProps {
  // The theme/scale half, forwarded as one prop rather than eighteen.
  themeSettings: ThemeSettings;
  themeSelectorClassName?: string;
  iconsOrderButtonClassName?: string;

  // "Open target page" — hidden mid-save, as before.
  viewHref: string;
  showViewLink: boolean;
  containerClassName?: string;
  viewLinkClassName?: string;

  // The voting-limit trio. The campaign checkbox is not a plain toggle — its
  // handler also recolours the key field's label — so it keeps its own prop.
  campaignEnabled: boolean;
  onChangeCampaign: ChangeHandler;
  campaignTtl: string;
  onChangeCampaignTtl: ChangeHandler;
  campaignKey: string;
  onChangeCampaignKey: ChangeHandler;

  // The seven plain toggles below all write one c-key and nothing else, so they
  // share a single handler keyed by that c-key instead of seven near-identical
  // one-line callbacks in the container.
  noVoting: boolean;
  whistleblower: boolean;
  dropZeroNps: boolean;
  dropNegativeNps: boolean;
  noAutosubmit: boolean;
  showNpsFooter: boolean;
  locationEnabled: boolean;
  onToggle: (cKey: string, checked: boolean) => void;
}

type ToggleKey = 'noVoting' | 'whistleblower' | 'dropZeroNps' | 'dropNegativeNps'
  | 'noAutosubmit' | 'showNpsFooter' | 'locationEnabled';

// `spaced` marks the rows the original separated with a 0.4em gap.
const TOGGLES: Array<{
  key: ToggleKey; cKey: string; label: string; redisid: string; title: string; spaced?: boolean;
}> = [
  { key: 'noVoting', cKey: 'c46', label: 'target.editor.label.noVoting', redisid: 'novoting', title: 'Disable voting' },
  { key: 'whistleblower', cKey: 'c63', label: 'target.editor.label.whistleblower', redisid: 'whistleblower', title: 'Whistleblower target' },
  { key: 'dropZeroNps', cKey: 'c86', label: 'target.editor.label.dropZeroNPS', redisid: 'dropNPSnull', title: 'Drop NPS null', spaced: true },
  { key: 'dropNegativeNps', cKey: 'c87', label: 'target.editor.label.dropNegativeNPS', redisid: 'dropNPSnegaive', title: 'Drop NPS negative' },
  { key: 'noAutosubmit', cKey: 'c88', label: 'target.editor.label.noAutosubmitWhenEmpty', redisid: 'noAutosubmit', title: 'Disable autosubmit' },
  { key: 'showNpsFooter', cKey: 'c79', label: 'target.editor.label.showNPSfooter', redisid: 'showNPSfooter', title: 'Show NPS footer' },
  { key: 'locationEnabled', cKey: 'gps', label: 'target.editor.label.locationEnabled', redisid: 'enablegps', title: 'Enable GPS-coordinates', spaced: true },
];

const SuperuserSettingsPanel = (props: SuperuserSettingsPanelOwnProps & WithTranslation) => {
  const {
    themeSettings, themeSelectorClassName, iconsOrderButtonClassName,
    viewHref, showViewLink, containerClassName, viewLinkClassName,
    campaignEnabled, onChangeCampaign, campaignTtl, onChangeCampaignTtl,
    campaignKey, onChangeCampaignKey, onToggle, t,
  } = props;

  return (
    <div className={containerClassName} style={{
      border: "1px solid red",
      padding: "0.5rem"
    }}>
      <ThemeSettingsPanel
        {...themeSettings}
        themeSelectorClassName={themeSelectorClassName}
        buttonClassName={iconsOrderButtonClassName} />

      {showViewLink &&
        <a className={clsx(viewLinkClassName, 'button success')}
          href={viewHref}
          title="Open target page"
          target="_blank"
        ><i className="fa fa-eye" aria-hidden="true"></i> {t('button.view')}</a>}

      <div style={{ height: "0.2em" }}></div>

      <div>
        <label>{t('target.editor.label.votingLimit')}</label>
        <input type="checkbox" data-redisid="campain"
          title="Disable voting"
          checked={campaignEnabled}
          onChange={onChangeCampaign}
        />
      </div>

      <div>
        {/* id is load-bearing: the campaign-key handler recolours this label. */}
        <label id="campain-ttl-label">{t('target.editor.label.votingLimitTime')}</label>
        <input type="number" min="0" max="31556952" style={{ textAlign: "end" }} data-redisid="campainttl"
          value={campaignTtl}
          title="Set how long time is needed in seconds before a user may vote again. Maximun timeout is one year. If set to zero, then next vote limited forever."
          onChange={onChangeCampaignTtl} />
      </div>

      <div>
        <label id="campain-name-label">{t('target.editor.label.votingLimitKey')}</label>
        <input type="text" data-redisid="campainname"
          value={campaignKey}
          title="Set election campain name."
          onChange={onChangeCampaignKey} />
      </div>
      <div style={{ height: "0.4em" }}></div>

      {TOGGLES.map(({ key, cKey, label, redisid, title, spaced }) => (
        <React.Fragment key={cKey}>
          {spaced && <div style={{ height: "0.4em" }}></div>}
          <div>
            <label>{t(label)}</label>
            <input type="checkbox" data-redisid={redisid}
              title={title}
              checked={props[key]}
              onChange={event => onToggle(cKey, event.target.checked)}
            />
          </div>
        </React.Fragment>
      ))}
      <div style={{ height: "0.4em" }}></div>
    </div>
  );
};

export default withTranslation('common')(SuperuserSettingsPanel);
